import { ref, watch, type Ref } from 'vue'

import { ApiRequestError } from '@/services/http'
import { fetchCustomerAutogiro, type MandateAwaitingSignature, type PlannedDebitAccount } from '@/services/autogiro'

/** Fetches immediately and again whenever `userId` changes. A 404 means the user isn't a customer. */
export const useCustomerAutogiro = (userId: Ref<string>) => {
  const isLoading = ref(true)
  const isNotFound = ref(false)
  const hasError = ref(false)
  const account = ref<PlannedDebitAccount | null>(null)
  const mandateAwaitingSignature = ref<MandateAwaitingSignature | null>(null)

  watch(
    userId,
    async (id) => {
      isLoading.value = true
      isNotFound.value = false
      hasError.value = false

      try {
        const response = await fetchCustomerAutogiro(id)
        // A slower response for a previous user must not overwrite the current one.
        if (id !== userId.value) return
        account.value = response.plannedDebitAccount
        mandateAwaitingSignature.value = response.mandateAwaitingSignature
      } catch (err) {
        if (id !== userId.value) return
        if (err instanceof ApiRequestError && err.status === 404) {
          isNotFound.value = true
        } else {
          console.error('Failed to load autogiro', err)
          hasError.value = true
        }
      } finally {
        if (id === userId.value) isLoading.value = false
      }
    },
    { immediate: true },
  )

  return { isLoading, isNotFound, hasError, account, mandateAwaitingSignature }
}
